import { create as noUiSliderCreate } from 'nouislider'
import type { useFilterState } from './useFilterState'
import type { EstateFilter } from '~/types/api/EstateListing'

export function useRangeSliderState(settings: {
  filterState: ReturnType<typeof useFilterState>
  filterKey: string
  serverFilterValues: EstateFilter
  useLogarithmicScale?: boolean
}) {
  const filterKey = ref(settings.filterKey)
  const filterState = settings.filterState
  const serverFilterValues = settings.serverFilterValues
  const useLogarithmicScale = settings.useLogarithmicScale ?? true

  const bounds = computed(() => ({
    min: Math.floor(parseFloat(serverFilterValues[`${filterKey.value}_from`])),
    max: Math.ceil(parseFloat(serverFilterValues[`${filterKey.value}_to`])),
  }))

  const requestFilterFields = computed(() => ({
    from: `${filterKey.value}_from`,
    to: `${filterKey.value}_to`,
  }))

  const sliderRef = ref<HTMLElement | null>(null)
  const noUiSliderInstance = ref<ReturnType<typeof noUiSliderCreate> | null>(
    null
  )

  const initialValues = {
    from:
      parseInt(
        filterState.filterValues.value[requestFilterFields.value.from]
      ) || undefined,
    to:
      parseInt(filterState.filterValues.value[requestFilterFields.value.to]) ||
      undefined,
  }

  const from = ref(initialValues.from || bounds.value.min) as Ref<number>
  const to = ref(initialValues.to || bounds.value.max) as Ref<number>

  const inputFrom = ref(initialValues.from?.toString()) as Ref<
    string | undefined
  >
  const inputTo = ref(initialValues.to?.toString()) as Ref<string | undefined>

  function changeFilterKey(
    newFilterKey: string,
    newValueMultiplier: number = 1
  ) {
    if (newFilterKey === filterKey.value) {
      return
    }

    filterState.actions.deleteValue(requestFilterFields.value.from)
    filterState.actions.deleteValue(requestFilterFields.value.to)

    const isFromBoundary = from.value === bounds.value.min
    const isToBoundary = to.value === bounds.value.max

    filterKey.value = newFilterKey

    from.value = Math.floor(from.value * newValueMultiplier)
    to.value = Math.ceil(to.value * newValueMultiplier)

    if (isFromBoundary || from.value <= bounds.value.min) {
      from.value = bounds.value.min
      inputFrom.value = undefined
    } else {
      inputFrom.value = from.value.toString()
    }

    if (isToBoundary || to.value >= bounds.value.max) {
      to.value = bounds.value.max
      inputTo.value = undefined
    } else {
      inputTo.value = to.value.toString()
    }

    sendValuesToFilter()
    submitFilter()
    rebuildSlider()
  }

  function onFromInputUpdate() {
    inputFrom.value = (inputFrom.value || '').replace(/\D/g, '')

    const parsedValue = parseInt(inputFrom.value)

    if (!inputFrom.value) {
      from.value = bounds.value.min
      inputFrom.value = undefined

      if (noUiSliderInstance.value) {
        noUiSliderInstance.value.set([
          bounds.value.min,
          to.value || bounds.value.max,
        ])
      }

      return
    } else {
      if (parsedValue < bounds.value.min) {
        from.value = bounds.value.min
      } else if (parsedValue > bounds.value.max) {
        from.value = bounds.value.max - 1
        inputFrom.value = from.value.toString()
      } else {
        from.value = parsedValue
      }

      if (parsedValue >= to.value) {
        from.value = to.value - 1
      }
    }

    if (noUiSliderInstance.value) {
      noUiSliderInstance.value.set([from.value || bounds.value.min, to.value])
    }

    sendValuesToFilter()
    submitFilter()
  }

  function onToInputUpdate() {
    inputTo.value = (inputTo.value || '').replace(/\D/g, '')

    const parsedValue = parseInt(inputTo.value)

    if (!inputTo.value) {
      to.value = bounds.value.max
      inputTo.value = undefined

      if (noUiSliderInstance.value) {
        noUiSliderInstance.value.set([
          from.value || bounds.value.min,
          bounds.value.max,
        ])
      }
    } else {
      if (parsedValue < bounds.value.min) {
        to.value = bounds.value.min
      } else if (parsedValue > bounds.value.max) {
        to.value = bounds.value.max
        inputTo.value = to.value.toString()
      } else {
        to.value = parsedValue
      }

      if (parsedValue <= from.value) {
        to.value = from.value + 1
      }
    }

    if (noUiSliderInstance.value) {
      noUiSliderInstance.value.set([from.value, to.value || bounds.value.max])
    }

    sendValuesToFilter()
    submitFilter()
  }

  function onFromInputChange() {
    onFromInputUpdate()

    setFromInputValues()
    sendValuesToFilter()
    submitFilter()
  }

  function onToInputChange() {
    onToInputUpdate()

    setToInputValues()
    sendValuesToFilter()
    submitFilter()
  }

  function onFromInputBlur() {
    onFromInputChange()
  }

  function onToInputBlur() {
    onToInputChange()
  }

  function onSliderSlide(values: (string | number)[], handle: number) {
    from.value = Math.floor(parseFloat(values[0].toString()))
    to.value = Math.ceil(parseFloat(values[1].toString()))

    setFromInputValues(handle === 0)
    setToInputValues(handle === 1)
  }

  function onSliderChange(values: (string | number)[]) {
    from.value = Math.floor(parseFloat(values[0].toString()))
    to.value = Math.ceil(parseFloat(values[1].toString()))

    setFromInputValues()
    setToInputValues()
    sendValuesToFilter()
    submitFilter()
  }

  function setFromInputValues(ignoreBoundary = false) {
    if (!ignoreBoundary && isFromBoundary()) {
      inputFrom.value = undefined
    } else {
      inputFrom.value = formatNumber(from.value)
    }
  }

  function setToInputValues(ignoreBoundary = false) {
    if (!ignoreBoundary && isToBoundary()) {
      inputTo.value = undefined
    } else {
      inputTo.value = formatNumber(to.value)
    }
  }

  function isFromBoundary() {
    return from.value === bounds.value.min
  }

  function isToBoundary() {
    return to.value === bounds.value.max
  }

  function areValuesBoundary() {
    return isFromBoundary() && isToBoundary()
  }

  function setSliderHandles() {
    if (!noUiSliderInstance.value) return

    noUiSliderInstance.value.set([from.value, to.value])
  }

  function sendValuesToFilter() {
    if (areValuesBoundary()) {
      filterState.actions.deleteValue(requestFilterFields.value.from)
      filterState.actions.deleteValue(requestFilterFields.value.to)
    } else {
      filterState.actions.updateValue(
        requestFilterFields.value.from,
        from.value
      )
      filterState.actions.updateValue(requestFilterFields.value.to, to.value)
    }
  }

  function submitFilter() {
    filterState.actions.submitFilter()
  }

  function _generateLogarithmicRange(min: number, max: number) {
    const logSteps = (() => {
      const differenceMap = {
        100: 2,
        1000: 3,
        10000: 4,
        100000: 5,
        1000000: 6,
      }

      for (const [key, value] of Object.entries(differenceMap)) {
        if (max - min < parseInt(key)) {
          return value
        }
      }

      return 1
    })()

    // Validate input
    if (max <= 0 || min >= max || logSteps <= 2 || !useLogarithmicScale) {
      return {
        min: [min],
        max: [max],
      }
    }

    // Calculate the scale
    const logMin = min === 0 ? 0 : Math.log(min)
    const logMax = Math.log(max)
    const scale = (logMax - logMin) / (logSteps - 1)

    const range = {} as any

    for (let i = 0; i < logSteps; i++) {
      let value
      let key

      if (i === 0) {
        key = 'min'
        value = min
      } else if (i === logSteps - 1) {
        key = 'max'
        value = max
      } else {
        key = `${Math.round((i / (logSteps - 1)) * 100)}%`
        value = Math.round(Math.exp(logMin + i * scale))
      }

      range[key] = [value]
    }

    return range
  }

  async function rebuildSlider() {
    if (!noUiSliderInstance.value) {
      return
    }

    destroySlider()

    await nextTick()

    initializeSlider(sliderRef.value)
  }

  function initializeSlider(ref: HTMLElement | null) {
    if (!ref) {
      return
    }

    sliderRef.value = ref

    noUiSliderInstance.value = noUiSliderCreate(sliderRef.value, {
      start: [from.value || bounds.value.min, to.value || bounds.value.max],
      connect: true,
      step: 1,
      margin: 1,
      range: _generateLogarithmicRange(bounds.value.min, bounds.value.max),
    })

    noUiSliderInstance.value.on('start', onSliderSlide)
    noUiSliderInstance.value.on('slide', onSliderSlide)
    noUiSliderInstance.value.on('change', onSliderChange)
  }

  function destroySlider() {
    if (!noUiSliderInstance.value) {
      return
    }

    noUiSliderInstance.value.off('start')
    noUiSliderInstance.value.off('slide')
    noUiSliderInstance.value.off('change')
    noUiSliderInstance.value.destroy()
  }

  const isFiltered = computed(() => {
    return (
      typeof filterState.filterValues.value[requestFilterFields.value.from] !==
        'undefined' ||
      typeof filterState.filterValues.value[requestFilterFields.value.to] !==
        'undefined'
    )
  })

  function onReset() {
    from.value = bounds.value.min
    to.value = bounds.value.max

    inputFrom.value = undefined
    inputTo.value = undefined

    if (noUiSliderInstance.value) {
      noUiSliderInstance.value.set([bounds.value.min, bounds.value.max])
    }

    sendValuesToFilter()
    submitFilter()
  }

  watch(
    [
      () => filterState.filterValues.value[requestFilterFields.value.from],
      () => filterState.filterValues.value[requestFilterFields.value.to],
    ],
    ([newValueFrom, newValueTo]) => {
      if (
        parseInt(newValueFrom) === from.value &&
        parseInt(newValueTo) === to.value
      ) {
        return
      }

      from.value = parseInt(newValueFrom) || bounds.value.min
      to.value = parseInt(newValueTo) || bounds.value.max

      setFromInputValues()
      setToInputValues()
      setSliderHandles()
    }
  )

  return {
    filterKey,
    noUiSliderInstance,
    bounds,
    changeFilterKey,
    onFromInputUpdate,
    onToInputUpdate,
    onFromInputBlur,
    onToInputBlur,
    onFromInputChange,
    onToInputChange,
    sendValuesToFilter,
    submitFilter,
    initializeSlider,
    destroySlider,
    isFiltered,
    onReset,
    inputValues: {
      from: inputFrom,
      to: inputTo,
    },
    inputPlaceholder: computed(() => ({
      from: formatNumber(bounds.value.min),
      to: formatNumber(bounds.value.max),
    })),
    dropdownPlaceholderValue: computed(() => {
      const fromFormatted = formatNumber(
        from.value !== undefined ? from.value : bounds.value.min,
        { minimumFractionDigits: 0 }
      )
      const toFormatted = formatNumber(
        to.value !== undefined ? to.value : bounds.value.max,
        { minimumFractionDigits: 0 }
      )

      return `${fromFormatted} - ${toFormatted}`
    }),
  }
}
